import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X, Plus, Minus } from 'lucide-react';

const MentalMath = ({ exercise, onAnswer }) => {
  const { a, b, operation, options } = exercise;
  const correctAnswer = operation === 'add' ? a + b : a - b;
  
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  }, [exercise]);

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    const correct = answer === correctAnswer;
    setIsCorrect(correct);
    setShowFeedback(true);

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getButtonClass = (option) => {
    if (!showFeedback) {
      return 'btn-number-default hover:border-primary hover:scale-105';
    }
    if (option === correctAnswer) {
      return 'btn-number-correct';
    }
    if (option === selectedAnswer && !isCorrect) {
      return 'btn-number-incorrect animate-shake';
    }
    return 'btn-number-default opacity-50';
  };

  const questionText = operation === 'add' 
    ? QUESTIONS['mental-math-add']
    : QUESTIONS['mental-math-subtract'];

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Question */}
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-8 text-center"
      >
        {questionText}
      </motion.h2>

      {/* Math Expression */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="mb-10 flex items-center gap-4"
      >
        {/* First number */}
        <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-button">
          <span className="text-3xl font-display font-bold text-primary-foreground">
            {a}
          </span>
        </div>

        {/* Operator */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring' }}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            operation === 'add' ? 'bg-correct-light' : 'bg-secondary-light'
          }`}
        >
          {operation === 'add' 
            ? <Plus className="w-6 h-6 text-success" />
            : <Minus className="w-6 h-6 text-secondary-foreground" />
          }
        </motion.div>

        {/* Second number */}
        <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center shadow-soft">
          <span className="text-3xl font-display font-bold text-accent-foreground">
            {b}
          </span>
        </div>

        {/* Equals */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-3xl font-display font-bold text-muted-foreground"
        >
          =
        </motion.div>

        {/* Answer placeholder */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: 'spring' }}
          className="w-16 h-16 rounded-2xl border-3 border-dashed border-border flex items-center justify-center"
        >
          <span className="text-3xl font-display font-bold text-muted-foreground">
            ?
          </span>
        </motion.div>
      </motion.div>

      {/* Answer Options */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="flex gap-4 justify-center flex-wrap"
      >
        {options.map((option, index) => (
          <motion.button
            key={option}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.7 + index * 0.1, type: 'spring' }}
            onClick={() => handleAnswer(option)}
            disabled={showFeedback}
            className={getButtonClass(option)}
          >
            {showFeedback && option === correctAnswer && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center"
              >
                <Check className="w-4 h-4 text-card" />
              </motion.span>
            )}
            {showFeedback && option === selectedAnswer && !isCorrect && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4 text-card" />
              </motion.span>
            )}
            {option}
          </motion.button>
        ))}
      </motion.div>

      {/* Feedback */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MentalMath;
